import { useEffect, useRef, useState } from "react";
import "./styles.scss";

type FoodEntry = {
  id: string;
  name: string;
  city: string;
  country: string;
  status: "tried" | "want";
  rating: number | null;
  visitDates?: string[];
  imageUrl?: string;
  thumbnailUrl?: string;
};

const FOOD_API = "https://food-blog-api.alextoub.workers.dev";
const SLIDE_COUNT = 5;

const latestVisit = (entry: FoodEntry) => [...(entry.visitDates || [])].sort().pop() || "";

export const selectLatestFood = (entries: FoodEntry[]) => entries
  .filter((entry) => entry.status === "tried" && latestVisit(entry) && (entry.thumbnailUrl || entry.imageUrl))
  .sort((a, b) => latestVisit(b).localeCompare(latestVisit(a)))
  .slice(0, SLIDE_COUNT);

const readJson = async (url: string, signal: AbortSignal): Promise<unknown> => {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return response.json();
};

const loadFood = async (signal: AbortSignal): Promise<FoodEntry[]> => {
  const data = await readJson(`${FOOD_API}/restaurants`, signal) as { restaurants?: FoodEntry[] };
  if (!Array.isArray(data.restaurants)) throw new Error("Invalid restaurants response");
  return selectLatestFood(data.restaurants);
};

const formatDate = (date: string) => {
  const parsed = new Date(`${date}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? date : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(parsed);
};

const ProjectVisual = () => {
  const [food, setFood] = useState<FoodEntry[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const trackRef = useRef<HTMLDivElement>(null);
  const siteUrl = "https://food.toubiana.dev/";

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    loadFood(controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setFood(result);
        setStatus(result.length ? "ready" : "error");
      })
      .catch(() => { if (!controller.signal.aborted) setStatus("error"); });
    return () => controller.abort();
  }, []);

  const move = (direction: number) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.firstElementChild as HTMLElement | null;
    const distance = (card?.getBoundingClientRect().width || 230) + 12;
    track.scrollBy({ left: direction * distance, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  };

  return (
    <div className="project-visual" role="region" aria-roledescription="carousel" aria-label="Latest visits from Alex's Food Journal">
      <div className="live-preview">
        <div className="live-preview-header">
          <span className="live-preview-brand">Alex's Food Journal</span>
          <span className="live-preview-tag">LATEST VISITS</span>
        </div>
        {status === "loading" && <div className="live-preview-status" role="status">Loading the latest places…</div>}
        {status === "error" && <div className="live-preview-status" role="status"><span>Recent places are unavailable right now.</span><a href={siteUrl} target="_blank" rel="noreferrer">Explore the live project</a></div>}
        {status === "ready" && <div className="live-preview-track" ref={trackRef} tabIndex={0} role="group" aria-label="Scroll through the latest places">
          {food.map((entry) => (
            <a className="live-preview-card food-slide-link" key={entry.id} href={`${siteUrl}restaurant/${encodeURIComponent(entry.id)}`} target="_blank" rel="noreferrer" aria-label={`View ${entry.name} on Alex's Food Journal`}>
              <div className="food-slide-photo"><img src={entry.thumbnailUrl || entry.imageUrl} alt={entry.name} loading="lazy" /></div>
              <div className="food-slide-copy">
                <span className="live-preview-eyebrow">{formatDate(latestVisit(entry))} · {entry.city}</span>
                <strong>{entry.name}</strong>
                <span className="food-slide-rating">{entry.rating != null ? `★ ${entry.rating.toFixed(1)} / 5` : "✓ Tried"}<span>VIEW →</span></span>
              </div>
            </a>
          ))}
        </div>}
        {status === "ready" && <div className="live-preview-controls">
          <span>{String(food.length).padStart(2, "0")} RECENT VISITS</span>
          <span className="live-preview-hint">SCROLL TO EXPLORE</span>
          <div className="live-preview-arrows"><button type="button" onClick={() => move(-1)} aria-label="Scroll places left">←</button><button type="button" onClick={() => move(1)} aria-label="Scroll places right">→</button></div>
        </div>}
      </div>
      <span className="live-preview-source">LIVE DATA FROM FOOD.TOUBIANA.DEV</span>
    </div>
  );
};

export default ProjectVisual;
